import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import { cn } from "@/utilities/core/cn";

/**
 * Maps a typed slot name to its corresponding CSS utility class.
 *
 * Each entry must match a `@utility` block in `src/app/(app)/tailwind.css`.
 * Adding a new surface slot is a two-step change:
 *   1. Add an entry here.
 *   2. Add a matching `@utility` block in tailwind.css.
 *
 * Element slots (buttons, links, inputs) are NOT in this map — they have
 * their own React components (`Button`, etc.) with prop-based variant APIs.
 */
const SURFACE_CLASSES = {
    pageBackground: "page-background-surface",
    header: "header-surface",
    secondaryHeader: "secondary-header-surface",
    brandRow: "brand-row-surface",
    footer: "footer-surface",
    card: "bg-card",
    matchCard: "match-card-surface",
    playerCard: "player-card-surface",
    gamificationCard: "gamification-card-surface",
    challengeZone: "challenge-zone-surface",
    modal: "modal-surface",
    table: "table-surface",
} as const;

export type SurfaceSlot = keyof typeof SURFACE_CLASSES;

type SurfaceOwnProps<T extends ElementType> = {
    slot: SurfaceSlot;
    as?: T;
    className?: string;
    children?: ReactNode;
};

type SurfaceProps<T extends ElementType> = SurfaceOwnProps<T> &
    Omit<ComponentPropsWithoutRef<T>, keyof SurfaceOwnProps<T>>;

/**
 * Themed surface wrapper. Renders an element of type `as` (default `div`)
 * with the slot's CSS utility class applied, plus any caller-supplied className.
 *
 * Sets a `data-surface={slot}` attribute as an opt-in escape hatch for any
 * descendant that needs context-aware styling via CSS attribute selectors.
 */
export function Surface<T extends ElementType = "div">({
    slot,
    as,
    className,
    children,
    ...rest
}: SurfaceProps<T>) {
    const Tag = (as || "div") as ElementType;
    return (
        <Tag className={cn(SURFACE_CLASSES[slot], className)} data-surface={slot} {...rest}>
            {children}
        </Tag>
    );
}
